import { Component, OnDestroy, OnInit } from '@angular/core';
import { AlertController, ModalController, PopoverController } from '@ionic/angular';
import { Observable, Subscription, map } from 'rxjs';
import { DeviceService } from 'src/app/core/services/behavior/device.service';
import { LocalizarPedidoInputService } from 'src/app/core/services/behavior/localizar-pedido-input.service';
import { ModaisService } from 'src/app/core/services/behavior/modais.service';
import { NavbarService } from 'src/app/core/services/behavior/navbar.service';
import { StorageService } from 'src/app/core/services/behavior/storage.service';
import { DatetimeService } from 'src/app/core/services/http/datetime.service';

import { PedidoService } from 'src/app/core/services/http/pedido.service';
import { OutrasOperacoesPopover, OutrasOperacoesPopoverRoleEnum } from 'src/app/shared/components/outras-operacoes/outras-operacoes.popover';
import { PagarContaComponent } from 'src/app/shared/components/pagar-conta/pagar-conta.component';
import { SelecionarProdutoComponent, SelecionarProdutoDataClose } from 'src/app/shared/components/selecionar-produto/selecionar-produto.component';
import { TransferenciaMesaComponent, TransferenciaMesaDataClose } from 'src/app/shared/components/transferencia-mesa/transferencia-mesa.component';
import { ModalRoleEnum } from 'src/app/shared/enums/modal-role.enum';
import { TipoAtendimentoEnum } from 'src/app/shared/enums/tipo-atendimento.enum';
import { ModalResult } from 'src/app/shared/interfaces/modal-result.interface';
import { DeviceViewModel } from 'src/app/shared/models/device.model';
import { FormaPgtoRequestV1 } from 'src/app/shared/models/forma-pgto.model';
import { MesasOcupadasModel } from 'src/app/shared/models/mesas-ocupadas.model';
import { PedidoModel, TransferenciaMesaRequest } from 'src/app/shared/models/pedido.model';
import { PessoaModel } from 'src/app/shared/models/pessoa.model';
import { UsuarioModel } from 'src/app/shared/models/usuario.model';
import { DialogStateUtil } from 'src/app/shared/utils/dialog-state.util';
import { HelpersUtil } from 'src/app/shared/utils/helpers.util';

declare var bootstrap: any;

@Component({
    selector: 'app-mesas',
    templateUrl: './mesas.component.html',
    styleUrls: ['./mesas.component.scss']
})
export class MesasComponent implements OnInit, OnDestroy {
    private subs$: Subscription[] = [];
    public device: DeviceViewModel = new DeviceViewModel();

    mesasOcupadas$: Observable<MesasOcupadasModel[]>;

    private _isHandlingClick = false;

    private podeCancelarPedido = false;

    constructor(
        private _alertCtrl: AlertController,
        private modalCtrl: ModalController,
        private popoverCtrl: PopoverController,

        private _localizarPedidoInputService: LocalizarPedidoInputService,
        private _modaisService: ModaisService,
        private pedidoService: PedidoService,
        private _navbarService: NavbarService,
        private _storageService: StorageService,
        private deviceService: DeviceService,
        private datetimeService: DatetimeService,
    ) { }

    public calcularTempo(tempo) {
        return HelpersUtil.formatarTempoDecorrido(tempo);
    }

    public ngOnDestroy(): void {
        this.subs$.forEach(sub$ => sub$.unsubscribe());
    }

    public async ngOnInit(): Promise<void> {
        this.load();

        HelpersUtil.executeAfterDelay(() => this.inicializarTooltips(), 800);

        this._acionarFocoNumeroMesaInput();

        const usuarioLogado = await this._storageService.get('user') as UsuarioModel;

        this.podeCancelarPedido = usuarioLogado.pdV_CancelarPedido;
    }

    public async outrasOperacoesPopover(event: Event, mesa: MesasOcupadasModel) {        
        if (this._isHandlingClick) return;
        this._isHandlingClick = true;        

        try {
            const popover = await this.popoverCtrl.create({
                component: OutrasOperacoesPopover,
                event: event,
                side: 'top',
                alignment: 'center',
                size: 'auto',
                cssClass: 'tamanho-popover'
            });

            await popover.present();

            const { role } = await popover.onDidDismiss();

            switch (role) {
                case OutrasOperacoesPopoverRoleEnum.AdicionarProduto:
                    await this.modalSelecionarProdutos(mesa);
                    break;

                case OutrasOperacoesPopoverRoleEnum.Imprimir:
                    this.imprimir(mesa);
                    break;
                case OutrasOperacoesPopoverRoleEnum.PagarConta:
                    await this.modalPagarConta(mesa);
                    break;
                case OutrasOperacoesPopoverRoleEnum.TransferenciaMesa:
                    await this.modalTransferenciaMesa(mesa);
                    break;
                case OutrasOperacoesPopoverRoleEnum.CancelarPedido:
                    await this._cancelarPedido(mesa.numeroComanda);
                    break;
            }
        } finally {
            this._isHandlingClick = false;
            this._acionarFocoNumeroMesaInput();
        }
    }

    private async _cancelarPedido(numeroComanda: number) {
        if (!this.podeCancelarPedido) {
            await this.showErrorMessage('Você não tem permissão para cancelar o pedido. Por favor, selecione um usuário que tenha autorização.');

            const confirmarUsuarioDialogDataClose = await this._modaisService.modalConfirmarUsuarioDialogAsync();

            if (confirmarUsuarioDialogDataClose.role === ModalRoleEnum.CANCEL)
                return;

            if (!confirmarUsuarioDialogDataClose.data.usuario.pdV_CancelarPedido)
                return await this.showErrorMessage('O usuário selecionado também não possui a permissão desejada.');
        }

        this.pedidoService.cancelarPedido(numeroComanda).subscribe(res => {
            if (res) {
                this.reload();
            }
        })
    }

    private async showErrorMessage(msg: string) {
        const alert = await this._alertCtrl.create({
            header: 'ATENÇÃO',
            message: msg,
            buttons: ['OK']
        });

        await alert.present();

        await alert.onWillDismiss();
    }
    public reload() {
        this.load();

        HelpersUtil.executeAfterDelay(() => this.inicializarTooltips(), 1000);
    }

    private _acionarFocoNumeroMesaInput() {

        HelpersUtil.executeAfterDelay(() => this._navbarService.focarNumeroMesaInput$.next(true));

    }

    private imprimir(mesaSelecionada: MesasOcupadasModel): void {
        const sub$ = this.pedidoService.imprimirPedido(mesaSelecionada.numeroComanda)
            .subscribe(() => this.reload());

        this.subs$.push(sub$);
    }

    private inicializarTooltips() {
        const tooltipTriggerList: any = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl));
    }

    private load(): void {
        this.mesasOcupadas$ = this.pedidoService
            .obterMesasOcupadas()
            .pipe(
                map(l => l.success ? l.data : []),
            )
    }

    private async modalPagarConta(mesaSelecionada: MesasOcupadasModel): Promise<void> {
        const pedido: PedidoModel = new PedidoModel(
            {
                numeroComanda: mesaSelecionada.numeroComanda,
                cliente: new PessoaModel({
                    nome: mesaSelecionada.nomeCliente
                })
            });

        const modal = await this.modalCtrl.create({
            component: PagarContaComponent,
            componentProps: {
                pedido: pedido,
                tipoAtendimento: TipoAtendimentoEnum.MESA
            },
            cssClass: 'full-screen-dialog'
        });

        DialogStateUtil.dialogIsOpen();
        await modal.present();

        const modalResult: ModalResult<any> = await modal.onWillDismiss();

        DialogStateUtil.dialogIsClosed();

        if (modalResult.role === ModalRoleEnum.CONFIRM) {
            this.load();
        }

        this._acionarFocoNumeroMesaInput();
    }

    private async modalSelecionarProdutos(mesaSelecionada: MesasOcupadasModel): Promise<void> {
        const pedido: PedidoModel = new PedidoModel({ numeroComanda: mesaSelecionada.numeroComanda });

        const modal = await this.modalCtrl.create({
            component: SelecionarProdutoComponent,
            componentProps: {
                pedido: pedido,
                tipoAtendimento: TipoAtendimentoEnum.MESA
            },
            cssClass: 'full-screen-dialog',
            backdropDismiss: false,
            keyboardClose: false
        });

        DialogStateUtil.dialogIsOpen();
        await modal.present();

        const modalResult: ModalResult<SelecionarProdutoDataClose> = await modal.onWillDismiss();
        DialogStateUtil.dialogIsClosed();
        
        if (modalResult.role === ModalRoleEnum.CONFIRM) {
            const formaPgto = new FormaPgtoRequestV1();
            modalResult.data.pedido.FormaPgtoOldV1 = formaPgto;
            modalResult.data.pedido.terminalId = this.deviceService.device.idDispositivo;;

            const datahora = await this.datetimeService.obterDataHora();
            modalResult.data.pedido.dataHora = datahora;

            const sub$ = this.pedidoService.enviarPedido(modalResult.data.pedido, TipoAtendimentoEnum.MESA).subscribe(() => this.reload());;

            this.subs$.push(sub$);
        }

        this._acionarFocoNumeroMesaInput();
    }

    private async modalTransferenciaMesa(mesaSelecionada: MesasOcupadasModel): Promise<void> {
        const modal = await this.modalCtrl.create({
            component: TransferenciaMesaComponent,
            componentProps: {
                mesa: mesaSelecionada
            },
            cssClass: 'full-screen-dialog'
        });

        DialogStateUtil.dialogIsOpen();
        await modal.present();

        const modalResult: ModalResult<TransferenciaMesaDataClose> = await modal.onWillDismiss();

        DialogStateUtil.dialogIsClosed();

        if (modalResult.role === ModalRoleEnum.CONFIRM) {
            const request = {
                numeroComanda: mesaSelecionada.numeroComanda,
                numeroComandaDestino: modalResult.data.numeroComandaDestino,
                codigosVendasDet: modalResult.data.codigosDet
            } as TransferenciaMesaRequest;

            const sub$ = this.pedidoService.transferenciaMesa(request).subscribe(() => this.reload());

            this.subs$.push(sub$);
        }

        this._acionarFocoNumeroMesaInput();
    }
}
